import { inject, Injectable } from '@angular/core';
import { ActionDisabledCondition, ActionItemOptions, UserAction, UserStoreService } from '@mediahaven/shared';
import {
    ApiFragment,
    Mh2Entity,
    NavigationLink,
    RecordTypeStructure,
    TaskKey,
    UserFunction,
    UserFunctions
} from '@stylehaven/components';
import { EMPTY, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ActionService } from '../../abstract/action.service.interface';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';
import { PublishActionSandbox } from '@common/sandboxes/actions/publish-action/publish-action.sandbox';

@Injectable({ providedIn: 'root' })
export class PublishActionService extends ActionService {
    protected override readonly action: MediaHavenActionType = MediaHavenActionType.PUBLISH;

    // Services
    private readonly userStoreService: UserStoreService = inject(UserStoreService);
    private readonly publishActionSandbox: PublishActionSandbox = inject(PublishActionSandbox);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /*  Publish
     *  Available when:
     *   * It is in concept
     *   * User has PUBLISH_MEDIA userfunction
     *   * It is NOT deleted
     *   * It is not a representation
     *   * It is not archived
     *
     *  If has executable Evaluation task
     *  Disabled when:
     *   * Not all required fields set.
     *   * Is in a Mh2Collection
     *
     *  If does not have executable Evaluation task
     *  Disabled when:
     *   * Is pending in another workflow
     *   * Is not publishable
     *   * Is a Fragment
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return this.getUserFunctions().pipe(map(userFunctions => this.userActionBuilder(record, userFunctions)));
    }

    private getUserFunctions(): Observable<UserFunctions> {
        return this.userStoreService.getUserFunctions();
    }

    public userActionBuilder(record: ApiFragment, userFunctions: UserFunctions): UserAction {
        const isConcept: boolean = record.isConcept();

        const hasPublishMedia: boolean = userFunctions.PUBLISH_MEDIA;
        const isPublishable: boolean = record.isPublishable();
        const isRepresentation: boolean = record.getRecordStructure() === RecordTypeStructure.Representation;

        const isDeleted: boolean = record.isDeleted();
        const modelValid: boolean = record.hasValidModel();
        const isPending: boolean = record.hasPendingTask();
        const hasExecutableEvaluationTask: boolean =
            isPending &&
            record
                .getTasks()
                .some(
                    task =>
                        task.TypeId === TaskKey.EvaluateMedia &&
                        task.AssigneeFunction &&
                        userFunctions[task.AssigneeFunction as UserFunction]
                );

        const isFragment = !!record.getMainFragmentId();

        const availableConditions: boolean[] = [isConcept, hasPublishMedia, !isDeleted, !isRepresentation];

        let disableConditions: ActionDisabledCondition[];
        const notPublishableReason: string | null = record.getContextReasonCode('IsPublishable');
        const notPublishableTooltip: string = notPublishableReason
            ? `general.${notPublishableReason}`
            : 'general.blocked-status';

        if (hasExecutableEvaluationTask) {
            disableConditions = [{ condition: !modelValid, tooltip: 'general.invalid-model' }];
        } else {
            disableConditions = [
                { condition: isPending, tooltip: 'general.pending-task' },
                { condition: !isPublishable, tooltip: notPublishableTooltip },
                { condition: isFragment, tooltip: 'publish.is-fragment' }
            ];
        }

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        const isPublishable$: Observable<boolean> = from(this.publishActionSandbox.validatePublishable(record));

        return isPublishable$.pipe(
            switchMap(isPublishable => (isPublishable ? this.onPublish(record, actionItemOptions) : EMPTY))
        );
    }

    private onPublish(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        this.initBusyToast(record, actionItemOptions, true);

        return this.publishActionSandbox.publishRecord(record);
    }

    // #region Labels

    public override getLabelKey(target: ApiFragment): string {
        let label: string = this.translate('actions.publish');

        if (target.isNewCollection()) {
            label = this.translate('actions.intellectual.publish');
        }
        return label;
    }

    public override getIcon(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return target.isConcept() && actionItemOptions.withIcon ? 'paper-plane' : '';
    }

    public override getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.publish';
    }

    public override getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public override getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public override getSuccessToastLink(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): NavigationLink | undefined {
        return undefined;
    }

    public override getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }

    // #endregion
}
